/** CrmRefuteEvidenceModal (E1b-0) — the ⚑ refute dialog for ONE evidence row on a CRM record,
 * shared by the Evidence sub-tab and the consolidated record timeline.
 *
 * ⛔ ONE COPY, DELIBERATELY. It was born inside `CrmEvidenceTimeline` and lifted out when the stream
 * needed the same affordance: two copies is how two doors onto one writer come to disagree about what
 * a refute records — the same reasoning `crm_lists._REFUTE_REASON_MAX` already gives for not inventing
 * a second reason vocabulary server-side. (The Deals tab keeps its OWN modal: it talks to the
 * ungoverned `/deal-cards/…/unbind-event/…` door and offers the rebind target picker this one refuses.)
 *
 * ⛔ MOUNT IT AT THE SURFACE'S ROOT, NEVER INSIDE THE ROW. A successful refute flips the row to
 * `resolution_status='noise'`, which `_load_evidence` filters out, and the mutation invalidates the
 * evidence query — so the row LEAVES the list on success. A modal owned by that row unmounts with it,
 * taking the "✓ Removed from this deal" confirmation with it before anyone can read it. Same failure
 * class as the timeline's action error, which had to move to the root because `onDone()` could unmount
 * its host in the same tick. Both call sites therefore hold `refuteFor` state themselves.
 *
 * Two of the old Deals modal's three options ship: "doesn't belong to this deal" and "just noise" —
 * both are a plain unbind server-side, the reason text is what distinguishes them. The third,
 * "belongs to a different deal", needs the Deals-tab-coupled target picker
 * (`/api/deal-cards/search`, ungoverned), so re-routing evidence stays on that tab.
 */
import { useState } from 'react';

import { useRefuteRecordEvidence } from '../../hooks/useCrm';
import styles from './crm.module.css';

/** The server's own words when it refuses. The refute route raises
 * `HTTPException(400, detail={"error": …, "message": …})`, so the useful string is `detail.detail.message`
 * — the same shape (and the same trap) `CrmRecordPanelContainer.applyError` handles: an ApiError's
 * `.message` is only "API error: 400 Bad Request". A governance refusal answers a bare string instead. */
export function refuteError(e: unknown, fallback: string): string {
  const detail = (e as { detail?: { detail?: string | { message?: string } } } | null)?.detail?.detail;
  if (typeof detail === 'string' && detail) return detail;
  const message = (detail as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

const REFUTE_REASONS: readonly { key: string; label: string; text: string }[] = [
  { key: 'not_this_deal', label: "Doesn't belong to this deal", text: "doesn't belong to this deal" },
  { key: 'noise', label: 'Just noise (forward, OOO, FYI)', text: 'just noise (forward, OOO, FYI)' },
];

export function CrmRefuteEvidenceModal({
  listId, entryId, eventLogId, subject, onClose,
}: {
  listId: number;
  entryId: number;
  eventLogId: number;
  /** The row's subject, for the "which email am I detaching" line. */
  subject: string | null;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState(REFUTE_REASONS[0].key);
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const refute = useRefuteRecordEvidence(listId);
  // The server requires a reason and stores it verbatim (free text, ≤500 — there is no reason enum on
  // either surface). The radio provides the sentence; an optional note is appended, as on the Deals tab.
  const picked = REFUTE_REASONS.find((r) => r.key === choice) ?? REFUTE_REASONS[0];
  const reason = `${picked.text}${note.trim() ? ` — ${note.trim()}` : ''}`.slice(0, 500);
  const submit = () => {
    if (refute.isPending || done) return;
    refute.mutate({ entryId, eventLogId, reason }, {
      onSuccess: (d) => {
        const dismissed = d?.auto_dismissed?.length ?? 0;
        const swept = d?.swept_siblings ?? 0;
        // Queue-only Phase B (owner ruling 2026-08-20) — the re-aggregation this
        // refute triggers is enqueued, not inline; say so when it fired.
        const queued = (d?.aggregated?.length ?? 0) > 0;
        setDone([
          '✓ Removed from this deal',
          swept > 0 ? `(also ${swept} other mailbox cop${swept === 1 ? 'y' : 'ies'})` : '',
          dismissed > 0 ? `— auto-dismissed ${dismissed} orphan candidate${dismissed === 1 ? '' : 's'}` : '',
          queued ? 'Suggestions will refresh shortly.' : '',
        ].filter(Boolean).join(' '));
      },
    });
  };
  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div className={styles.modalCard} role="dialog" aria-label="Refute this evidence"
        onMouseDown={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Refute this evidence</h3>
        <div className={styles.evRefuteSubject}>{subject || '(no subject)'}</div>
        {done ? (
          <div className={styles.evRefuteDone} role="status">{done}</div>
        ) : (
          <>
            {REFUTE_REASONS.map((r) => (
              <label key={r.key} className={styles.modalCheck}>
                <input type="radio" name="crm-refute-reason" checked={choice === r.key}
                  onChange={() => setChoice(r.key)} />
                {r.label}
              </label>
            ))}
            <input className={styles.modalInput} maxLength={400} value={note} autoFocus
              placeholder="Anything to add? (optional)"
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }} />
            <div className={styles.evRefuteHint}>
              Moving this email to a different deal is still done on the Deals tab.
            </div>
          </>
        )}
        {refute.isError && (
          <div className={styles.modalError} role="alert">
            {refuteError(refute.error, 'Could not refute that evidence row.')}
          </div>
        )}
        <div className={styles.modalActions}>
          <button className={styles.gridMiniBtn} onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button className={styles.gridPageBtn} disabled={refute.isPending} onClick={submit}>
              {refute.isPending ? 'Refuting…' : 'Refute'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CrmRefuteEvidenceModal;
